import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, PartyPopper } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ASSETS, EVENT_TYPES, BUDGET_RANGES, SERVICE_OPTIONS } from "@/data/assets";
import api from "@/lib/apiClient";

const field = "bg-transparent border-0 border-b border-brand-navy/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-brand-gold";

export default function Booking() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_type: "", event_date: "", guest_count: "", budget_range: "", services: [], message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleService = (s) => setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.event_type) return toast.error("Please select an event type.");
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", form);
      toast.success(data.message);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail ? String(err.response.data.detail) : "Something went wrong.");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div data-testid="booking-success" className="min-h-screen grid place-items-center pt-24 pb-16 px-5">
        <div className="text-center max-w-md">
          <PartyPopper className="text-brand-gold mx-auto" size={54} />
          <h1 className="font-serif text-4xl text-brand-navy mt-6">Request Received!</h1>
          <p className="mt-4 text-brand-ink/70">Thank you, {form.name.split(" ")[0]}. Our team will reach out shortly to craft your perfect event.</p>
          <a href="/" className="mt-8 inline-flex bg-brand-navy text-white font-semibold px-8 py-3.5 rounded-sm">Back to Home</a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="booking-page">
      <section className="relative pt-32 pb-14">
        <img src={ASSETS.mandap} alt="Plan your event" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-brand-navy/85" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <p className="overline mb-3">Event Booking</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight">Plan Your Dream Event</h1>
          <p className="mt-4 text-white/75 max-w-xl">Share a few details and our planners will design a tailored proposal for you.</p>
        </div>
      </section>

      <section className="py-16 max-w-3xl mx-auto px-5 sm:px-8">
        <form onSubmit={submit} data-testid="booking-form" className="space-y-8 bg-white border border-brand-navy/10 p-7 sm:p-10 rounded-sm">
          <div className="grid sm:grid-cols-2 gap-6">
            <div><Label className="text-xs uppercase tracking-widest text-brand-ink/60">Full Name *</Label><Input required data-testid="booking-name" value={form.name} onChange={set("name")} className={field} /></div>
            <div><Label className="text-xs uppercase tracking-widest text-brand-ink/60">Phone *</Label><Input required data-testid="booking-phone" value={form.phone} onChange={set("phone")} className={field} /></div>
          </div>
          <div><Label className="text-xs uppercase tracking-widest text-brand-ink/60">Email *</Label><Input required type="email" data-testid="booking-email" value={form.email} onChange={set("email")} className={field} /></div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <Label className="text-xs uppercase tracking-widest text-brand-ink/60">Event Type *</Label>
              <select required data-testid="booking-event-type" value={form.event_type} onChange={set("event_type")} className={`${field} w-full h-10 text-sm`}>
                <option value="">Select…</option>
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div><Label className="text-xs uppercase tracking-widest text-brand-ink/60">Event Date</Label><Input type="date" data-testid="booking-date" value={form.event_date} onChange={set("event_date")} className={field} /></div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div><Label className="text-xs uppercase tracking-widest text-brand-ink/60">Guest Count</Label><Input data-testid="booking-guests" placeholder="e.g. 250" value={form.guest_count} onChange={set("guest_count")} className={field} /></div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-brand-ink/60">Budget Range</Label>
              <select data-testid="booking-budget" value={form.budget_range} onChange={set("budget_range")} className={`${field} w-full h-10 text-sm`}>
                <option value="">Select…</option>
                {BUDGET_RANGES.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-brand-ink/60 mb-3 block">Services Needed</Label>
            <div className="flex flex-wrap gap-2.5">
              {SERVICE_OPTIONS.map((s) => (
                <button type="button" key={s} data-testid={`booking-service-${s.split(" ")[0].toLowerCase()}`} onClick={() => toggleService(s)}
                  className={`px-3.5 py-2 text-sm rounded-sm border transition-colors flex items-center gap-1.5 ${form.services.includes(s) ? "bg-brand-navy text-white border-brand-navy" : "border-brand-navy/20 text-brand-ink/70 hover:border-brand-gold"}`}>
                  {form.services.includes(s) && <CheckCircle2 size={14} />} {s}
                </button>
              ))}
            </div>
          </div>

          <div><Label className="text-xs uppercase tracking-widest text-brand-ink/60">Tell us more</Label><Textarea rows={4} data-testid="booking-message" value={form.message} onChange={set("message")} className={field} /></div>

          <button disabled={loading} data-testid="booking-submit" className="w-full inline-flex justify-center items-center gap-2 bg-brand-gold text-brand-navy font-semibold px-8 py-4 rounded-sm hover:-translate-y-0.5 transition-transform disabled:opacity-60">
            {loading && <Loader2 size={18} className="animate-spin" />} Submit Event Request
          </button>
        </form>
      </section>
    </div>
  );
}
